import React, { useState } from 'react'
import './navigation.css'
import logo from '../../resources/images/logo.png'
import { FaEnvelope, FaBars, FaTimes } from 'react-icons/fa'
import { Link, useNavigate } from 'react-router-dom'
import { useAuthContext } from '../../Hooks/useAuthContext'

/* ── Inline SVG icons for drawer nav links ──────────────── */
const IconHome = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
    <polyline points="9 22 9 12 15 12 15 22"/>
  </svg>
)
const IconAbout = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <circle cx="12" cy="12" r="10"/>
    <line x1="12" y1="8" x2="12" y2="12"/>
    <line x1="12" y1="16" x2="12.01" y2="16"/>
  </svg>
)
const IconTestimonials = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
  </svg>
)
const IconPolicy = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
    <polyline points="14 2 14 8 20 8"/>
    <line x1="16" y1="13" x2="8" y2="13"/>
    <line x1="16" y1="17" x2="8" y2="17"/>
  </svg>
)
const IconContact = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
    <polyline points="22,6 12,13 2,6"/>
  </svg>
)
const IconDashboard = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
    <rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>
  </svg>
)

function Navigation() {
  const [drawerOpen, setDrawerOpen] = useState(false)
  const { user, dispatch }          = useAuthContext()
  const navigate                    = useNavigate()

  const logout = () => {
    dispatch({ type: 'LOGOUT', payload: null })
    localStorage.setItem('user', null)
  }

  const closeDrawer = () => setDrawerOpen(false)

  const navLinks = [
    { to: '/',               label: 'Home',          Icon: IconHome },
    { to: '/about-us',       label: 'About',         Icon: IconAbout },
    { to: '/testimonials',   label: 'Testimonials',  Icon: IconTestimonials },
    { to: '/privacy-policy', label: 'Privacy Policy',Icon: IconPolicy },
    { to: '/contact',        label: 'Contact',       Icon: IconContact },
  ]

  return (
    <div className="navbar">

      {/* ── Top utility bar (desktop only) ──────────────────── */}
      <div className="nav-topbar">
        <div className="nav-topbar__links">
          <Link to="/testimonials">Reviews</Link>
          <Link to="/privacy-policy">Policy</Link>
        </div>
        <div className="nav-topbar__contact">
          <FaEnvelope size={12} />
          info@nextwavesfinsedge.com
        </div>
      </div>

      {/* ── Lower main bar ──────────────────────────────────── */}
      <div className="nav-lower">

        {/* Logo */}
        <Link to="/" className="nav-logo">
          <img src={logo} alt="JFE Bank" />
        </Link>

        {/* Desktop links */}
        <nav className="nav-links">
          {navLinks.map(({ to, label }) => (
            <Link to={to} key={to}>{label}</Link>
          ))}
          {user && (
            <Link to="/dashboard">Dashboard</Link>
          )}
        </nav>

        {/* Desktop auth */}
        <div className="nav-auth">
          {user ? (
            <button className="nav-btn nav-btn--danger" onClick={logout}>
              Logout
            </button>
          ) : (
            <>
              <Link to="/login">
                <button className="nav-btn nav-btn--ghost">Sign In</button>
              </Link>
              <Link to="/register">
                <button className="nav-btn nav-btn--navy">Sign Up</button>
              </Link>
            </>
          )}
        </div>

        {/* Hamburger */}
        <button
          className="nav-hamburger"
          onClick={() => setDrawerOpen(true)}
          aria-label="Open menu"
        >
          <FaBars size={22} />
        </button>

      </div>

      {/* ── Mobile drawer ───────────────────────────────────── */}
      {drawerOpen && (
        <div className="nav-drawer">
          {/* Backdrop */}
          <div className="nav-drawer__backdrop" onClick={closeDrawer} />

          {/* Panel */}
          <div className="nav-drawer__panel">

            {/* Header */}
            <div className="nav-drawer__head">
              <Link to="/" onClick={closeDrawer}>
                <img src={logo} alt="JFE Bank" />
              </Link>
              <button className="nav-drawer__close" onClick={closeDrawer} aria-label="Close menu">
                <FaTimes size={16} />
              </button>
            </div>

            {/* Nav links */}
            <nav className="nav-drawer__links">
              {navLinks.map(({ to, label, Icon }) => (
                <Link to={to} key={to} onClick={closeDrawer}>
                  <Icon />
                  {label}
                </Link>
              ))}
              {user && (
                <Link to="/dashboard" onClick={closeDrawer}>
                  <IconDashboard />
                  Dashboard
                </Link>
              )}
            </nav>

            {/* Auth */}
            <div className="nav-drawer__auth">
              {user ? (
                <button
                  className="nav-btn nav-btn--danger"
                  onClick={() => { logout(); closeDrawer() }}
                >
                  Logout
                </button>
              ) : (
                <>
                  <Link to="/login" onClick={closeDrawer}>
                    <button className="nav-btn nav-btn--ghost">Sign In</button>
                  </Link>
                  <Link to="/register" onClick={closeDrawer}>
                    <button className="nav-btn nav-btn--navy">Sign Up</button>
                  </Link>
                </>
              )}
            </div>

          </div>
        </div>
      )}

    </div>
  )
}

export default Navigation